import React, { useState, useMemo, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ColorPicker } from './ColorPicker';
import { PreviewPaneImport } from './PreviewPaneImport';
import { PrintInstructions } from './PrintInstructions';
import { DownloadDialog } from './DownloadDialog';
import { MOBILE_BREAKPOINT } from '../constants/print';
import { generatePdf } from '../utils/generatePdf';
import type { CardBackLogoType, CardBackStyle, ClassFeature, Feat, Item, Spell } from '../types';
import { SpellCardExpanded } from './SpellCardExpanded';
import { ItemCardExpanded } from './ItemCardExpanded';
import { ClassCardExpanded } from './ClassCardExpanded';
import { FeatCardExpanded } from './FeatCardExpanded';
import { useSelection } from '../context/useSelection';
import type { CardEntityByVariant, CardVariant, SelectedCard } from '../context/selectionContextDef';

type SelectableEntity = CardEntityByVariant[CardVariant];

// Import state from context
interface MultiTabImportCounts {
  spells: number;
  items: number;
  classes: number;
  feats: number;
}

interface MultiTabImportState {
  isLoading: boolean;
  error: string | null;
  successCounts: MultiTabImportCounts | null;
  warnings: string[];
}

// Props for rendering a list card
interface ListCardProps<T> {
  entity: T;
  isSelected: boolean;
  onToggle: (id: string) => void;
}

// Configuration for the page
export interface CardListPageConfig<T extends SelectableEntity> {
  /** The variant of card this page manages (needed for selection) */
  variant: CardVariant;
  /** Page CSS class name (e.g., "spells-page") */
  pageClassName: string;
  /** List CSS class name (e.g., "spells-list") */
  listClassName: string;
  /** Document title for print */
  documentTitle: string;
  /** Search placeholder text */
  searchPlaceholder: string;
  /** Empty state configuration */
  emptyState: {
    icon: string;
    title: string;
    description: string;
    instructions: string[];
  };
  /** Import configuration */
  import: {
    onImport: (url: string) => Promise<void>;
    onImportMultiple?: (urls: string[]) => Promise<void>;
    state: MultiTabImportState;
    onClearStatus: () => void;
    spellsCount: number;
    itemsCount: number;
    classesCount: number;
    featsCount?: number;
    onClearSpells: () => void;
    onClearItems: () => void;
    onClearClasses: () => void;
    onClearFeats?: () => void;
    combinedTemplateUrl: string;
  };
  /** Component to render a list card */
  ListCard: React.ComponentType<ListCardProps<T>>;
  /** Render function for the filters section (excluding search) */
  renderFilters: () => ReactNode;
}

interface CardListPageProps<T extends SelectableEntity> {
  /** All entities (base + custom) */
  allEntities: T[];
  /** Filtered and sorted entities based on current filter state */
  filteredEntities: T[];
  /** Configuration */
  config: CardListPageConfig<T>;
}

const PAGE_OPTIONS = [
  { label: 'Classes', path: '/classes' },
  { label: 'Feats', path: '/feats' },
  { label: 'Items', path: '/items' },
  { label: 'Spells', path: '/spells' },
] as const;

function PageSwitcher() {
  const location = useLocation();
  const navigate = useNavigate();

  const currentPath = location.pathname === '/' ? '/spells' : location.pathname;
  const currentLabel =
    PAGE_OPTIONS.find((o) => o.path === currentPath)?.label ?? 'Spells';

  return (
    <div className="filter-group page-switcher">
      <label>Category:</label>
      <select
        className="filter-select"
        value={currentLabel}
        onChange={(e) => {
          const option = PAGE_OPTIONS.find((o) => o.label === e.target.value);
          if (option) navigate(option.path);
        }}
      >
        {PAGE_OPTIONS.map((o) => (
          <option key={o.path} value={o.label}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function renderExpandedCard(
  selectedCard: SelectedCard,
  borderColor?: string,
  isPrintMode?: boolean
) {
  if (selectedCard.variant === 'spells') {
    return (
      <SpellCardExpanded
        spell={selectedCard.entity as Spell}
        borderColor={borderColor}
        isPrintMode={isPrintMode}
      />
    );
  }

  if (selectedCard.variant === 'items') {
    return (
      <ItemCardExpanded
        item={selectedCard.entity as Item}
        borderColor={borderColor}
        isPrintMode={isPrintMode}
      />
    );
  }

  if (selectedCard.variant === 'feats') {
    return (
      <FeatCardExpanded
        feat={selectedCard.entity as Feat}
        borderColor={borderColor}
        isPrintMode={isPrintMode}
      />
    );
  }

  return (
    <ClassCardExpanded
      classFeature={selectedCard.entity as ClassFeature}
      borderColor={borderColor}
      isPrintMode={isPrintMode}
    />
  );
}

export function CardListPage<T extends SelectableEntity>({
  allEntities,
  filteredEntities,
  config,
}: CardListPageProps<T>) {
  const {
    variant,
    pageClassName,
    listClassName,
    documentTitle,
    searchPlaceholder,
    emptyState,
    import: importConfig,
    ListCard,
    renderFilters,
  } = config;

  const {
    selectedCards,
    selectedCount,
    globalBorderColor,
    setGlobalBorderColor,
    isSelected,
    toggleCard,
    addCards,
    clearAllCards,
    getCardBorderColor,
    setCardBorderColor,
    applyBorderColorToAll,
  } = useSelection();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCardPicker, setActiveCardPicker] = useState<string | null>(null);
  const [isFilterPaneOpen, setIsFilterPaneOpen] = useState(false);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);

  const printRef = useRef<HTMLDivElement>(null);

  // Close filter pane when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > MOBILE_BREAKPOINT) {
        setIsFilterPaneOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter by search query
  const searchFilteredEntities = useMemo(() => {
    if (!searchQuery) return filteredEntities;
    const query = searchQuery.toLowerCase();
    return filteredEntities.filter(e => e.name.toLowerCase().includes(query));
  }, [filteredEntities, searchQuery]);

  const toggleEntity = useCallback((entityId: string) => {
    const entity = allEntities.find((candidate) => candidate.id === entityId);
    if (!entity) return;
    toggleCard(variant, entity);
  }, [allEntities, variant, toggleCard]);

  const selectAll = useCallback(() => {
    addCards(variant, searchFilteredEntities);
  }, [addCards, variant, searchFilteredEntities]);

  const clearAll = useCallback(() => {
    clearAllCards();
  }, [clearAllCards]);

  const printDocumentTitle = useMemo(() => {
    const variantSet = new Set(selectedCards.map((card) => card.variant));
    return variantSet.size <= 1 ? documentTitle : 'Mythic Mixed Cards';
  }, [documentTitle, selectedCards]);

  // PDF generation with native card back rendering
  const handleDownloadConfirm = useCallback(async (
    includeInstructions: boolean,
    includeCardBacks: boolean,
    logoType: CardBackLogoType,
    style: CardBackStyle
  ) => {
    if (isGeneratingPdf) return;
    
    setIsGeneratingPdf(true);
    
    try {
      // Build card back options for native rendering
      // Border colors are in the same order as selectedObjects
      // Use borderColors Map directly to avoid stale closure issues
      const cardBackOptions = includeCardBacks ? {
        enabled: true,
        logoType,
        style,
        borderColors: selectedCards.map(
          (card) => getCardBorderColor(card.variant, card.entity.id) || globalBorderColor
        ),
      } : undefined;
      
      await generatePdf(printRef, printDocumentTitle, includeInstructions, cardBackOptions);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    } finally {
      setIsGeneratingPdf(false);
      setIsDownloadDialogOpen(false);
    }
  }, [getCardBorderColor, globalBorderColor, isGeneratingPdf, printDocumentTitle, selectedCards]);

  return (
    <div className={pageClassName}>
      {/* Mobile overlay when filter pane is open */}
      {isFilterPaneOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setIsFilterPaneOpen(false)}
          aria-hidden="true"
        />
      )}
      
      {/* Mobile toggle button for filter pane */}
      <button
        className="mobile-filter-toggle"
        onClick={() => setIsFilterPaneOpen(!isFilterPaneOpen)}
        aria-label={isFilterPaneOpen ? 'Close filters' : 'Open filters'}
        aria-expanded={isFilterPaneOpen}
      >
        <span className="toggle-icon">{isFilterPaneOpen ? '✕' : '☰'}</span>
        <span className="toggle-text">{isFilterPaneOpen ? 'Close' : 'Filters'}</span>
        {selectedCount > 0 && !isFilterPaneOpen && (
          <span className="selection-badge">{selectedCount}</span>
        )}
      </button>
      
      <div className={`left-pane ${isFilterPaneOpen ? 'open' : ''}`}>
        <div className="filters-section">
          <PageSwitcher />
          <div className="filters-header">
            <div className="filter-group filters-header-search">
              <input
                type="search"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <button
              className="filters-toggle-btn"
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              aria-expanded={isFiltersExpanded}
              aria-label={isFiltersExpanded ? 'Collapse filters' : 'Expand filters'}
            >
              <span className={`filters-toggle-chevron ${isFiltersExpanded ? 'expanded' : ''}`}>▾</span>
            </button>
          </div>

          <div className={`filters-collapsible ${isFiltersExpanded ? 'expanded' : ''}`}>
            {renderFilters()}

            <div className="filter-actions">
              <button onClick={selectAll} className="action-btn">
                SELECT ALL ({searchFilteredEntities.length})
              </button>
              {selectedCount > 0 && (
                <button onClick={clearAll} className="action-btn clear-btn">
                  CLEAR ({selectedCount})
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={listClassName}>
          {searchFilteredEntities.map((entity) => (
            <ListCard
              key={entity.id}
              entity={entity}
              isSelected={isSelected(variant, entity.id)}
              onToggle={toggleEntity}
            />
          ))}
        </div>
      </div>

      <div className="main-pane">
        <div className="cards-header">
          <div className="cards-header-controls">
            <div className="border-color-control">
              <ColorPicker
                color={globalBorderColor}
                onChange={setGlobalBorderColor}
              />
              <button
                onClick={applyBorderColorToAll}
                className="apply-all-btn"
                title="Apply border color to all selected cards"
                disabled={selectedCount === 0}
              >
                Apply Border
              </button>
            </div>
            <button
              onClick={() => setIsDownloadDialogOpen(true)}
              className="download-btn"
              disabled={selectedCount === 0}
            >
              Download Cards
            </button>
            <PreviewPaneImport
              onImport={importConfig.onImport}
              onImportMultiple={importConfig.onImportMultiple}
              isLoading={importConfig.state.isLoading}
              error={importConfig.state.error}
              successCounts={importConfig.state.successCounts}
              warnings={importConfig.state.warnings}
              onClearStatus={importConfig.onClearStatus}
              spellsCount={importConfig.spellsCount}
              itemsCount={importConfig.itemsCount}
              classesCount={importConfig.classesCount}
              featsCount={importConfig.featsCount}
              onClearSpells={importConfig.onClearSpells}
              onClearItems={importConfig.onClearItems}
              onClearClasses={importConfig.onClearClasses}
              onClearFeats={importConfig.onClearFeats}
              combinedTemplateUrl={importConfig.combinedTemplateUrl}
            />
          </div>
        </div>
        {selectedCount === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">{emptyState.icon}</div>
            <h2>{emptyState.title}</h2>
            <p>{emptyState.description}</p>
            <div className="instructions">
              {emptyState.instructions.map((instruction, i) => (
                <p key={i}>{instruction}</p>
              ))}
            </div>
          </div>
        ) : (
          <div className="selected-cards">
            {selectedCards.map((card) => (
              <div key={card.key} className="card-wrapper">
                <div className="card-preview-container">
                  {renderExpandedCard(
                    card,
                    getCardBorderColor(card.variant, card.entity.id) || globalBorderColor
                  )}
                  <div
                    className="card-delete-overlay"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCard(card.variant, card.entity);
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="40"
                      height="40"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </div>
                </div>
                <div className="card-border-picker">
                  <button
                    className="card-color-btn"
                    style={{
                      backgroundColor: getCardBorderColor(card.variant, card.entity.id) || globalBorderColor,
                    }}
                    onClick={() => setActiveCardPicker(activeCardPicker === card.key ? null : card.key)}
                    title="Set border color"
                  />
                  {activeCardPicker === card.key && (
                    <div className="card-color-dropdown">
                      <ColorPicker
                        color={getCardBorderColor(card.variant, card.entity.id) || globalBorderColor}
                        onChange={(color) => setCardBorderColor(card.variant, card.entity.id, color)}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hidden print area - card backs are rendered natively by jsPDF for performance */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          <PrintInstructions />
          <div className="print-area">
            {/* Card fronts only - backs are drawn natively in generatePdf */}
            <div className="print-fronts">
              {selectedCards.map((card) => {
                const borderColor = getCardBorderColor(card.variant, card.entity.id) || globalBorderColor;
                return (
                  <React.Fragment key={card.key}>
                    {renderExpandedCard(card, borderColor, true)}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <DownloadDialog
        isOpen={isDownloadDialogOpen}
        onClose={() => setIsDownloadDialogOpen(false)}
        onConfirm={handleDownloadConfirm}
        previewColor={globalBorderColor}
        isGenerating={isGeneratingPdf}
      />
    </div>
  );
}
